/**
 * Device API Client
 * Handles communication with the backend for device type management
 */

import type { DeviceDto, DeviceResponse } from "@shared/contracts/api";
import type { AppDeviceType } from "@shared/contracts/domain";
import { getDeviceFingerprint } from "./fingerprint";
import { ApiError, apiRequest } from "./api/client";

// Re-export types for convenience
export type DeviceType = AppDeviceType;
export type DeviceInfo = DeviceDto;

/**
 * Get device type for current user and device fingerprint
 */
export async function getDevice(): Promise<DeviceInfo | null> {
  const fingerprint = await getDeviceFingerprint();

  try {
    const data = await apiRequest<DeviceResponse>(
      `/api/device?fingerprint=${encodeURIComponent(fingerprint)}`,
      {},
    );
    return data.device;
  } catch (error) {
    if (error instanceof ApiError) {
      console.error(
        "[DeviceAPI] Failed to get device:",
        error.status,
        error.details,
      );
    } else {
      console.error("[DeviceAPI] Error getting device:", error);
    }
    return null;
  }
}

/**
 * Set device type for current user and device fingerprint
 */
export async function setDevice(
  deviceType: DeviceType,
  deviceName?: string
): Promise<DeviceInfo | null> {
  const fingerprint = await getDeviceFingerprint();

  try {
    const data = await apiRequest<DeviceResponse>("/api/device", {
      method: "POST",
      body: {
        fingerprint,
        deviceType,
        deviceName,
        userAgent: navigator.userAgent,
      },
    });
    return data.device;
  } catch (error) {
    if (error instanceof ApiError) {
      console.error(
        "[DeviceAPI] Failed to set device:",
        error.status,
        error.details,
      );
    } else {
      console.error("[DeviceAPI] Error setting device:", error);
    }
    return null;
  }
}
